import React, { useState, useEffect, useRef } from "react";
import { Zap, ShieldCheck, Globe, Cpu, Users, Eye, FileLock, Users2 } from "lucide-react";

export const WhyNovaCore = () => {
  const [activeCount, setActiveCount] = useState(-1);
  const containerRef = useRef(null);

  const features = [
    {
      title: "Fast Deployment Dispatch",
      icon: Zap,
      description: "Our core indexing matrices align and dispatch verified mission-critical personnel anywhere globally in under 72 hours."
    },
    {
      title: "Elite Technical Assessment",
      icon: Cpu,
      description: "Every candidate undergoes strict level 1-5 structural mechanical, electrical systems, and SCADA validation protocols."
    },
    {
      title: "Global Recruitment Pipelines",
      icon: Globe,
      description: "Leveraging sovereign networks spanning EMEA, APAC, and North American hyper-scale hubs for constant alignment."
    },
    {
      title: "Mission Critical Expertise",
      icon: ShieldCheck,
      description: "Our recruiters are electrical, thermal containment, and networking veterans who speak the exact language of facilities engineering."
    },
    {
      title: "Cloud Infrastructure Support",
      icon: Users,
      description: "Unparalleled access to specialists in virtualization, software defined networks, and dense optics pipelines."
    },
    {
      title: "AI Cluster Preparedness",
      icon: Users2,
      description: "Sourcing engineers specialized in liquid containment systems, InfiniBand matrices, and next-gen multi-MW high-compute complexes."
    },
    {
      title: "Security & Sovereignty Compliance",
      icon: FileLock,
      description: "All index profiles undergo extensive SOC-2, ISO-27001 background checks, and international safety guidelines clearance."
    },
    {
      title: "Long-Term Partnerships",
      icon: Eye,
      description: "Delivering permanent personnel solutions and technical operation consulting arrays to enterprise giants."
    }
  ];

  // Icons light up progressively tied to scroll position (like the process timeline)
  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      if (rect.top < viewportHeight * 0.8 && rect.bottom > 0) {
        const progress = Math.max(0, Math.min(1, (viewportHeight * 0.5 - rect.top) / rect.height));
        setActiveCount(Math.min(features.length - 1, Math.floor(progress * features.length)));
      }
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section 
      id="why"
      ref={containerRef}
      data-testid="why-novacore-section"
      className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-16 border-t border-white/5"
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-start">
        {/* Left Stick panel */}
        <div className="lg:sticky lg:top-32 flex flex-col items-start gap-4">
          <span className="text-xs font-mono uppercase tracking-[0.2em] text-[#65FFB6]">
            SOCIETAL VALUE MATRIX
          </span>
          <h2 className="text-3xl md:text-5xl font-outfit font-bold tracking-tight text-white leading-tight">
            Why Technology Giants Partner <br />With NovaCore.
          </h2>
          <p className="text-slate-400 font-manrope text-base max-w-md leading-relaxed mt-2">
            Traditional recruiting systems look at resumes. NovaCore validates operational readiness under mission-critical constraints.
          </p>
        </div>

        {/* Right 2-column feature cards */}
        <div 
          data-testid="why-novacore-grid"
          className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-6"
        >
          {features.map((feat, idx) => {
            const IconComponent = feat.icon;
            const isLit = idx <= activeCount;

            return (
              <div
                key={idx}
                data-testid={`why-card-${idx}`}
                className="glass-panel p-6 rounded-2xl border border-white/10 flex flex-col items-start gap-3.5 bg-black/40 group hover:border-[#65FFB6]/30 hover:shadow-[0_0_20px_rgba(101,255,182,0.06)] transition-all duration-300"
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center border transition-all duration-500 group-hover:bg-[#65FFB6] group-hover:text-black group-hover:border-[#65FFB6] ${
                  isLit 
                    ? "bg-[#0B1F1A] border-emerald-500/40 text-[#65FFB6] shadow-[0_0_15px_rgba(101,255,182,0.3)]" 
                    : "bg-white/5 border-white/10 text-slate-500"
                }`}>
                  <IconComponent className="w-5 h-5" />
                </div>
                <h3 className="text-lg font-outfit font-semibold text-white group-hover:text-[#65FFB6] transition-colors duration-200">
                  {feat.title}
                </h3>
                <p className="text-sm font-manrope text-slate-400 leading-relaxed">
                  {feat.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
